import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import {
  money,
  monthName,
  serviceCatalogCostsDashboardQuery,
  type Money,
} from '../api/serviceCatalog'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'
import { CatalogHead } from './ServiceCatalogChrome'

/**
 * Tabloul de costuri - `Service_catalog::costsDashboard()`, macheta
 * `views/version-3/inventoree/service_catalog/costs_dashboard.php`.
 *
 * DE CE UN TABLOU SI NU RAPORTUL. Raportul raspunde "cat a costat fiecare
 * serviciu" - un tabel, de citit rand cu rand si de dus mai departe intr-o
 * factura. Tabloul raspunde altceva: unde se duc banii, ce s-a schimbat fata de
 * luna trecuta, care client si care categorie trag greu. Sunt doua intrebari,
 * si un tabel de o suta de randuri nu raspunde la a doua.
 *
 * PE MONEDA, PESTE TOT. Doi clienti facturati in monede diferite n-au un total
 * comun. Graficul deseneaza o SINGURA moneda - cea mai grea - fiindca doua
 * monede pe aceeasi axa ar desena o linie care nu inseamna nimic, si o spune
 * dedesubt.
 *
 * GRAFICUL E DIN BARE, ca in portal, nu dintr-o biblioteca: amCharts a iesit
 * din portal pe 6 septembrie 2026 si nu se aduce inapoi. Aceleasi clase
 * (`sc-chart`, `sc-col`, `sc-fill`), si acelasi stil, mutat din macheta in
 * `public/css/service_catalog.css`.
 *
 * "Work out a month" NU e aici, si nici in portal: a plecat la Settings >
 * Company Reports > Easeedesk Reports, langa raportul pe care il hraneste.
 */
export function ServiceCatalogCostsDashboardPage() {
  const [wanted, setWanted] = useState(0)

  const page = useQuery(serviceCatalogCostsDashboardQuery(wanted))
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(
    ['css/adm.css', 'css/service_shop.css', 'css/service_catalog.css'],
    shell.data?.version ?? '',
  )

  if (isForbidden(page.error)) return <Forbidden />
  if (!page.data) return <Spinner />

  const data = page.data

  /* Moneda cu cea mai mare suma da tonul cifrei mari si al graficului. */
  const main = data.totals.reduce<Money | null>((best, one) => (best && best.amount >= one.amount ? best : one), null)
  const mainCurrency = main?.currency ?? ''
  const mainTotal = main?.amount ?? 0
  const previousMain = data.previous.find((one) => one.currency === mainCurrency)?.amount ?? null

  const delta =
    previousMain !== null && previousMain > 0
      ? Math.round(((mainTotal - previousMain) * 100) / previousMain * 10) / 10
      : null

  const peak = data.trend.reduce(
    (top, row) => Math.max(top, row.amounts.find((one) => one.currency === mainCurrency)?.amount ?? 0),
    0,
  )

  return (
    <div id="content">
      <CatalogHead
        title="Service costs"
        icon="fa-coins"
        tone="blue-mauve"
        note="where the money goes, and what changed since last month"
        stats={[
          {
            value: mainCurrency ? money(mainTotal, mainCurrency) : '—',
            label: monthName(data.period),
            tone: 'blue-mauve',
          },
          {
            value: delta === null ? '—' : delta > 0 ? `+${delta}%` : `${delta}%`,
            label: `vs ${monthName(data.previousPeriod)}`,
            /* O crestere e rosie si o scadere e verde: la un tablou de costuri,
               "mai mult" e lucrul la care se uita cineva primul. */
            tone: delta === null ? 'grey' : delta > 0 ? 'red-intense' : 'green-mode',
          },
          { value: data.counts.services, label: 'service lines', tone: 'grey' },
        ]}
      />

      <div className="adm-bar">
        <div className="adm-field">
          <label htmlFor="scPeriod">Month</label>
          <select
            className="form-control"
            id="scPeriod"
            style={{ width: 180 }}
            value={data.period}
            onChange={(e) => setWanted(Number(e.target.value))}
          >
            {data.periods.length === 0 && <option value={0}>no month computed yet</option>}
            {data.periods.map((one) => (
              <option value={one} key={one}>
                {monthName(one)}
              </option>
            ))}
          </select>
        </div>

        <div className="adm-field">
          <label>&nbsp;</label>
          <a className="btn btn-default" href="/app/settings/company_reports/easeedesk_report">
            <i className="fas fa-table"></i> The report, line by line
          </a>
        </div>
      </div>

      {data.totals.length === 0 ? (
        <div className="adm-card adm-tone-grey">
          <div className="adm-empty">
            <i className="fas fa-coins"></i>
            <p>Nothing has been costed for this month.</p>
            {/*
              Golul spune de ce, nu doar ca. Costurile se calculeaza pe luna, la
              cerere; cine vede gol trebuie sa stie ca butonul care umple luna e
              in alt ecran, altfel crede ca s-a stricat asta.
            */}
            <span>
              Costs are worked out per month, on demand — with <strong>Work out a month</strong> on{' '}
              <a href="/app/settings/company_reports/easeedesk_report">the cost report</a>. If a month is missing,
              nobody has worked it out yet, or the services of this customer carry no cost rules.
            </span>
          </div>
        </div>
      ) : (
        <>
          <div className="row adm-tiles">
            {data.totals.map((one, index) => (
              <div className="col-md-3 col-sm-6" key={one.currency}>
                <div className={`dashboard-stat ${TONES[index % TONES.length]}`}>
                  <div className="visual">
                    <i className="fas fa-coins"></i>
                  </div>
                  <div className="details">
                    <div className="number">{Math.round(one.amount)}</div>
                    <div className="desc">Total {one.currency}</div>
                  </div>
                </div>
              </div>
            ))}

            <div className="col-md-3 col-sm-6">
              <div className="dashboard-stat blue-hoki">
                <div className="visual">
                  <i className="fa fa-cubes"></i>
                </div>
                <div className="details">
                  <div className="number">{data.counts.items}</div>
                  <div className="desc">Items billed</div>
                </div>
              </div>
            </div>

            <div className="col-md-3 col-sm-6">
              <div className="dashboard-stat grey">
                <div className="visual">
                  <i className="fa fa-users"></i>
                </div>
                <div className="details">
                  <div className="number">{data.counts.users}</div>
                  <div className="desc">Users</div>
                </div>
              </div>
            </div>
          </div>

          {data.trend.length > 1 && (
            <>
              <div className="adm-section adm-section-blue-mauve">
                <h4>
                  <i className="fas fa-chart-line"></i> Month by month
                </h4>
                <span className="adm-section-note">
                  the months that were actually worked out — a month nobody costed is not a zero, it is a gap
                </span>
              </div>

              <div className="adm-card adm-tone-blue-mauve">
                <div className="sc-chart">
                  {data.trend.map((row) => {
                    const amount = row.amounts.find((one) => one.currency === mainCurrency)?.amount ?? 0
                    /* Cel putin doi pixeli cand SE INTAMPLA ceva: o bara de zero
                       ar arata ca o luna care lipseste. */
                    const height = peak > 0 ? Math.max(2, Math.round((amount * 100) / peak)) : 0

                    return (
                      <div
                        className="sc-col"
                        key={row.period_ym}
                        title={`${monthName(row.period_ym)} · ${money(amount, mainCurrency)}`}
                      >
                        <span className="sc-val">{Math.round(amount).toLocaleString('en-US').replace(/,/g, ' ')}</span>
                        <div className="sc-fill" style={{ height: `${height}%` }}></div>
                        <span className="sc-lbl">
                          {monthName(row.period_ym).slice(0, 3)} {String(row.period_ym).slice(2, 4)}
                        </span>
                      </div>
                    )
                  })}
                </div>

                <p className="adm-muted" style={{ fontSize: 12, margin: '8px 0 0' }}>
                  Amounts in {mainCurrency}.
                  {data.totals.length > 1 && ' Other currencies are not drawn here — they do not share an axis.'}
                </p>
              </div>
            </>
          )}

          <div className="row">
            <div className="col-md-6">
              <div className="adm-section adm-section-green-mode">
                <h4>
                  <i className="fas fa-layer-group"></i> By category
                </h4>
                <span className="adm-section-note">which kind of service costs the most</span>
              </div>

              <div className="adm-card adm-tone-green-mode">
                <table className="table adm-list" width="100%">
                  <tbody>
                    {data.byCategory.map((row) => {
                      const share =
                        mainTotal > 0
                          ? Math.round(
                              ((row.amounts.find((one) => one.currency === mainCurrency)?.amount ?? 0) * 100)
                                / mainTotal,
                            )
                          : 0

                      return (
                        <tr key={row.name}>
                          <td>
                            <strong>{row.name}</strong>
                          </td>
                          <td className="text-right adm-nowrap">
                            {row.amounts.map((one) => (
                              <div key={one.currency}>{money(one.amount, one.currency)}</div>
                            ))}
                          </td>
                          <td style={{ width: 120 }}>
                            <div className="sc-bar">
                              <i style={{ width: `${share}%` }}></i>
                            </div>
                            <span className="adm-muted">{share}%</span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="col-md-6">
              <div className="adm-section adm-section-blue-hoki">
                <h4>
                  <i className="fas fa-building"></i> By customer
                </h4>
                <span className="adm-section-note">who the bill belongs to</span>
              </div>

              <div className="adm-card adm-tone-blue-hoki">
                <table className="table adm-list" width="100%">
                  <tbody>
                    {data.byCompany.map((row) => (
                      <tr key={row.name}>
                        <td>
                          <strong>{row.name}</strong>
                        </td>
                        <td className="text-right adm-nowrap">
                          {row.amounts.map((one) => (
                            <div key={one.currency}>{money(one.amount, one.currency)}</div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="adm-section adm-section-red-intense">
            <h4>
              <i className="fas fa-fire"></i> Where the money goes
            </h4>
            <span className="adm-section-note">the ten most expensive service lines this month</span>
          </div>

          <div className="adm-card adm-tone-red-intense">
            <table className="table adm-list" width="100%">
              <thead>
                <tr>
                  <th>Service</th>
                  <th style={{ width: 180 }}>Customer</th>
                  <th style={{ width: 150 }}>Category</th>
                  <th style={{ width: 70 }}>Items</th>
                  <th style={{ width: 70 }}>Users</th>
                  <th style={{ width: 80 }}>Tickets</th>
                  <th style={{ width: 130 }} className="text-right">
                    Cost
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.top.map((row) => (
                  <tr key={`${row.catalog_id}-${row.currency}`}>
                    <td>
                      <a href={`/app/service_catalog/costs/${row.catalog_id}`}>{row.service_name}</a>
                    </td>
                    <td className="adm-muted">{row.company_name}</td>
                    <td className="adm-muted">{row.category_name}</td>
                    <td>{row.ci_count}</td>
                    <td>{row.user_count}</td>
                    <td>{row.ticket_count}</td>
                    <td className="text-right">
                      <strong>{money(row.amount, row.currency)}</strong>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

/** `$tones` din macheta: patru culori care se rotesc pe monede. */
const TONES = ['blue-madison', 'green-mode', 'yellow', 'brown']
